import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatINR } from "@/lib/format";
import {
  Package, FileText, Receipt, LayoutGrid, Plus, Pencil, Trash2, Download,
  Upload, Paperclip, ExternalLink, FileDown, FileArchive, Search, AlertTriangle,
} from "lucide-react";
import { createPdfDoc, withDefaults, drawTotalsBlock, finalizePdf, fmtINR } from "@/lib/pdf";
import { todayLocalISO } from "@/lib/dates";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-700",
  sent: "bg-blue-100 text-blue-800",
  received: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-700",
  unpaid: "bg-red-50 text-red-700 border border-red-200",
  partial: "bg-amber-50 text-amber-800 border border-amber-200",
  paid: "bg-green-50 text-green-800 border border-green-200",
};

const emptyItem = () => ({
  _uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `it-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`,
  description: "", hsn: "", quantity: "", unit: "unit", rate: "",
});

export default function AdminProcurement() {
  const [tab, setTab] = useState("dashboard");
  const [vendors, setVendors] = useState([]);
  const [zones, setZones] = useState([]);
  const [pos, setPos] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({ zone: "", vendor: "", status: "", q: "" });
  const [invFilter, setInvFilter] = useState({ vendor: "", payment_status: "", q: "" });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [v, z, p, i, s] = await Promise.all([
        api.get("/vendors"), api.get("/zones"),
        api.get("/purchase-orders"), api.get("/invoices"),
        api.get("/procurement/summary"),
      ]);
      setVendors(v.data); setZones(z.data); setPos(p.data); setInvoices(i.data); setSummary(s.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Procurement" subtitle="Vendors · Purchase Orders · Invoices" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-white border border-gray-200 rounded-xl p-1">
              <TabsTrigger value="dashboard" data-testid="tab-proc-dashboard" className="data-[state=active]:bg-red-50 data-[state=active]:text-brand-red rounded-lg">
                <LayoutGrid className="h-4 w-4 mr-1.5" /> Dashboard
              </TabsTrigger>
              <TabsTrigger value="pos" data-testid="tab-proc-pos" className="data-[state=active]:bg-red-50 data-[state=active]:text-brand-red rounded-lg">
                <FileText className="h-4 w-4 mr-1.5" /> Purchase Orders
              </TabsTrigger>
              <TabsTrigger value="invoices" data-testid="tab-proc-invoices" className="data-[state=active]:bg-red-50 data-[state=active]:text-brand-red rounded-lg">
                <Receipt className="h-4 w-4 mr-1.5" /> Invoices
              </TabsTrigger>
              <TabsTrigger value="vendors" data-testid="tab-proc-vendors" className="data-[state=active]:bg-red-50 data-[state=active]:text-brand-red rounded-lg">
                <Package className="h-4 w-4 mr-1.5" /> Vendors
              </TabsTrigger>
            </TabsList>

            <TabsContent value="dashboard" className="mt-4">
              <DashboardTab summary={summary} loading={loading} />
            </TabsContent>
            <TabsContent value="vendors" className="mt-4">
              <VendorsTab vendors={vendors} onChange={loadAll} loading={loading} />
            </TabsContent>
            <TabsContent value="pos" className="mt-4">
              <POsTab pos={pos} vendors={vendors} zones={zones} filters={filters} setFilters={setFilters}
                onChange={loadAll} loading={loading} />
            </TabsContent>
            <TabsContent value="invoices" className="mt-4">
              <InvoicesTab invoices={invoices} pos={pos} vendors={vendors}
                filter={invFilter} setFilter={setInvFilter}
                onChange={loadAll} loading={loading} />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}

/* ---------- Dashboard ---------- */
function DashboardTab({ summary, loading }) {
  if (loading || !summary) return <Card className="p-8 text-center text-gray-500">Loading…</Card>;
  const t = summary.totals;
  const kpis = [
    { label: "Purchase Orders", value: t.po_count, sub: `${formatINR(t.po_total)} total`, color: "text-brand-red" },
    { label: "Invoices", value: t.invoice_count, sub: `${formatINR(t.invoice_total)} billed`, color: "text-blue-700" },
    { label: "Outstanding", value: formatINR(t.outstanding), sub: "unpaid amount", color: "text-amber-700" },
  ];

  const exportXlsx = async () => {
    try {
      const r = await api.get("/procurement/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `procurement_${todayLocalISO()}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Excel export failed"); }
  };
  const exportZip = async () => {
    try {
      const r = await api.get("/procurement/attachments.zip", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `procurement_attachments_${todayLocalISO()}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("ZIP export failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-gray-500">Aggregated across all POs & invoices</div>
        <div className="flex gap-2">
          <Button data-testid="proc-export-xlsx-btn" variant="outline" onClick={exportXlsx}>
            <FileDown className="h-4 w-4 mr-2" /> Export Excel
          </Button>
          <Button data-testid="proc-export-zip-btn" variant="outline" onClick={exportZip}>
            <FileArchive className="h-4 w-4 mr-2" /> Attachments ZIP
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {kpis.map(k => (
          <Card key={k.label} className="rounded-xl border-gray-200 bg-white p-5">
            <div className="text-[11px] uppercase font-bold tracking-[0.14em] text-gray-500">{k.label}</div>
            <div className={`font-display text-3xl font-black mt-1 ${k.color}`}>{k.value}</div>
            <div className="text-xs text-gray-500 mt-1">{k.sub}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-xl border-gray-200 bg-white p-5">
          <div className="font-display text-lg font-bold text-gray-900 mb-3">By Zone</div>
          <table className="w-full text-sm" data-testid="proc-zone-table">
            <thead className="text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
              <tr>
                <th className="text-left py-2">Zone</th><th className="text-right py-2">POs</th>
                <th className="text-right py-2">PO Total</th><th className="text-right py-2">Unpaid</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_zone.map(z => (
                <tr key={z.zone} className="border-b border-gray-50">
                  <td className="py-2 font-semibold">{z.zone}</td>
                  <td className="py-2 text-right">{z.po_count}</td>
                  <td className="py-2 text-right font-mono">{formatINR(z.po_total)}</td>
                  <td className={`py-2 text-right font-mono ${z.unpaid > 0 ? "text-amber-700" : "text-gray-500"}`}>{formatINR(z.unpaid)}</td>
                </tr>
              ))}
              {summary.by_zone.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-gray-500">No data yet</td></tr>}
            </tbody>
          </table>
        </Card>
        <Card className="rounded-xl border-gray-200 bg-white p-5">
          <div className="font-display text-lg font-bold text-gray-900 mb-3">Top Vendors</div>
          <table className="w-full text-sm" data-testid="proc-vendor-table">
            <thead className="text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
              <tr>
                <th className="text-left py-2">Vendor</th><th className="text-right py-2">POs</th>
                <th className="text-right py-2">PO Total</th><th className="text-right py-2">Unpaid</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_vendor.slice(0, 10).map(v => (
                <tr key={v.vendor} className="border-b border-gray-50">
                  <td className="py-2 font-semibold">{v.vendor}</td>
                  <td className="py-2 text-right">{v.po_count}</td>
                  <td className="py-2 text-right font-mono">{formatINR(v.po_total)}</td>
                  <td className={`py-2 text-right font-mono ${v.unpaid > 0 ? "text-amber-700" : "text-gray-500"}`}>{formatINR(v.unpaid)}</td>
                </tr>
              ))}
              {summary.by_vendor.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-gray-500">No data yet</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}

/* ---------- Vendors ---------- */
function VendorsTab({ vendors, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [q, setQ] = useState("");

  const openNew = () => { setForm({ id: null, name: "", contact_person: "", phone: "", email: "", gst_number: "", address: "", notes: "", status: "active" }); setOpen(true); };
  const openEdit = (v) => { setForm({ ...v }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Vendor name required");
    setSaving(true);
    try {
      const payload = { ...form }; delete payload.id;
      if (form.id) { await api.patch(`/vendors/${form.id}`, payload); toast.success("Vendor updated"); }
      else { await api.post("/vendors", payload); toast.success("Vendor added"); }
      setOpen(false); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!confirmDel) return;
    try { await api.delete(`/vendors/${confirmDel.id}`); toast.success("Deleted"); setConfirmDel(null); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const filtered = vendors.filter(v => !q || v.name.toLowerCase().includes(q.toLowerCase()) || (v.gst_number || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="relative max-w-sm w-full">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="vendor-search" placeholder="Search vendor or GST…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" />
        </div>
        <Button data-testid="add-vendor-btn" onClick={openNew} className="bg-brand-red hover:bg-[#B31721]">
          <Plus className="h-4 w-4 mr-2" /> Add Vendor
        </Button>
      </div>

      <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vendors-table">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-3">Vendor</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">GST</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="text-center py-8 text-gray-500">Loading…</td></tr>}
              {!loading && filtered.map(v => (
                <tr key={v.id} className="border-t border-gray-100" data-testid={`vendor-row-${v.name}`}>
                  <td className="px-4 py-3"><div className="font-semibold">{v.name}</div><div className="text-xs text-gray-500 truncate max-w-xs">{v.address}</div></td>
                  <td className="px-4 py-3">
                    <div className="text-sm">{v.contact_person || "—"}</div>
                    <div className="text-xs text-gray-500">{v.phone} {v.email && `· ${v.email}`}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{v.gst_number || "—"}</td>
                  <td className="px-4 py-3">
                    <Badge className={v.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-gray-100 text-gray-700 hover:bg-gray-100"}>{v.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button data-testid={`edit-vendor-${v.name}`} size="sm" variant="outline" onClick={() => openEdit(v)} className="mr-1"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                    <Button data-testid={`del-vendor-${v.name}`} size="sm" variant="outline" onClick={() => setConfirmDel(v)} className="text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (<tr><td colSpan={5} className="text-center py-8 text-gray-500">No vendors yet</td></tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      {form && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="vendor-dialog" className="max-w-lg rounded-2xl">
            <DialogHeader><DialogTitle>{form.id ? "Edit vendor" : "Add vendor"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Vendor name</label>
                <Input data-testid="vendor-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Contact person</label>
                <Input data-testid="vendor-contact-input" value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Phone</label>
                <Input data-testid="vendor-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Email</label>
                <Input data-testid="vendor-email-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">GST number</label>
                <Input data-testid="vendor-gst-input" value={form.gst_number} onChange={e => setForm({ ...form, gst_number: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Status</label>
                <select data-testid="vendor-status-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm">
                  <option value="active">active</option><option value="inactive">inactive</option>
                </select></div>
              <div className="col-span-2"><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Address</label>
                <Input data-testid="vendor-address-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Notes</label>
                <Input data-testid="vendor-notes-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="save-vendor-btn" onClick={save} disabled={saving} className="bg-brand-red hover:bg-[#B31721]">{form.id ? "Save" : "Add"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Delete vendor?</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700">Delete <b>{confirmDel?.name}</b>? Allowed only if no POs are linked.</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button data-testid="confirm-del-vendor" onClick={del} className="bg-red-600 hover:bg-red-700 text-white"><Trash2 className="h-4 w-4 mr-2" /> Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- Purchase Orders ---------- */
function POsTab({ pos, vendors, zones, filters, setFilters, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadingPoFile, setUploadingPoFile] = useState(false);
  const [uploadPoForm, setUploadPoForm] = useState(null);   // Upload PO fast-path

  const openNew = () => setForm({
    id: null, zone: zones[0]?.name || "", vendor_id: vendors[0]?.id || "",
    date: todayLocalISO(),
    delivery_address: "", items: [emptyItem()], tax_percent: 18, notes: "", status: "draft",
    attachment_path: null, attachment_name: null,
  });
  const openUpload = () => setUploadPoForm({
    vendor_id: vendors[0]?.id || "", zone: zones[0]?.name || "",
    date: todayLocalISO(),
    po_number: "", total: "", tax_percent: 18, notes: "",
    attachment_path: null, attachment_name: null,
  });
  useEffect(() => { if (form) setOpen(true); else setOpen(false); }, [form]);

  const openEdit = (po) => setForm({
    id: po.id, zone: po.zone, vendor_id: po.vendor_id, date: po.date,
    delivery_address: po.delivery_address || "",
    items: (po.items || []).map(it => ({
      ...emptyItem(), ...it,
      quantity: String(it.quantity), rate: String(it.rate),
    })),
    tax_percent: po.tax_percent, notes: po.notes || "", status: po.status,
    attachment_path: po.attachment_path || null,
    attachment_name: po.attachment_name || null,
  });

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const rmItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, ix) => ix !== i) }));
  const setItem = (i, k, v) => setForm(f => ({ ...f, items: f.items.map((it, ix) => ix === i ? { ...it, [k]: v } : it) }));

  const totals = useMemo(() => {
    if (!form) return { subtotal: 0, tax: 0, total: 0 };
    const sub = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.rate) || 0), 0);
    const tax = sub * (Number(form.tax_percent) || 0) / 100;
    return { subtotal: sub, tax, total: sub + tax };
  }, [form]);

  const save = async () => {
    if (!form.vendor_id) return toast.error("Vendor required");
    if (!form.zone) return toast.error("Zone required");
    const cleanItems = form.items
      .filter(it => it.description.trim() && Number(it.quantity) > 0 && Number(it.rate) >= 0)
      .map(it => ({ description: it.description.trim(), hsn: it.hsn || "", quantity: Number(it.quantity), unit: it.unit || "unit", rate: Number(it.rate) }));
    if (!cleanItems.length) return toast.error("At least one valid line item required");
    setSaving(true);
    try {
      const payload = { zone: form.zone, vendor_id: form.vendor_id, date: form.date, delivery_address: form.delivery_address,
        items: cleanItems, tax_percent: Number(form.tax_percent) || 0, notes: form.notes, status: form.status,
        attachment_path: form.attachment_path || null,
        attachment_name: form.attachment_name || null };
      if (form.id) { await api.patch(`/purchase-orders/${form.id}`, payload); toast.success("PO updated"); }
      else { await api.post("/purchase-orders", payload); toast.success("PO created"); }
      setForm(null); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!confirmDel) return;
    try { await api.delete(`/purchase-orders/${confirmDel.id}`); toast.success("Deleted"); setConfirmDel(null); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setStatus = async (po, status) => {
    try { await api.patch(`/purchase-orders/${po.id}`, { status }); toast.success(`Marked ${status}`); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadAttachment = async (po, file) => {
    if (!file) return;
    setUploadingId(po.id);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload-doc", fd);
      await api.patch(`/purchase-orders/${po.id}`, { attachment_path: data.path, attachment_name: data.original_name });
      toast.success("Attachment uploaded");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploadingId(null); }
  };

  // Upload-PO fast path — vendor + date + amount + reference + file. No line items.
  const pickUploadPoFile = async (file) => {
    if (!file) return;
    setUploadingPoFile(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload-doc", fd);
      setUploadPoForm(f => ({ ...f, attachment_path: data.path, attachment_name: data.original_name }));
      toast.success("File attached — fill the rest and save.");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploadingPoFile(false); }
  };
  const saveUploadPo = async () => {
    const f = uploadPoForm;
    if (!f.vendor_id) return toast.error("Vendor required");
    if (!f.attachment_path) return toast.error("Attach the PO document");
    if (!(Number(f.total) > 0)) return toast.error("Total must be > 0");
    setSaving(true);
    try {
      await api.post("/purchase-orders/upload", {
        vendor_id: f.vendor_id, zone: f.zone, date: f.date,
        po_number: f.po_number || null, total: Number(f.total),
        tax_percent: Number(f.tax_percent) || 0, notes: f.notes,
        attachment_path: f.attachment_path, attachment_name: f.attachment_name,
      });
      toast.success("PO uploaded"); setUploadPoForm(null); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const openFile = (path) => {
    const token = localStorage.getItem("bb_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`, "_blank");
  };

  const downloadPdf = async (po) => {
    const doc = await createPdfDoc();
    doc.setFontSize(16); doc.setTextColor(225, 29, 42);
    doc.text("Coke@9 OCT · Purchase Order", 12, 14);
    doc.setFontSize(9); doc.setTextColor(60);
    doc.text("Powered by BBIPL for CCI", 12, 20);
    doc.setFontSize(11); doc.setTextColor(20);
    doc.text(`PO #: ${po.po_number}`, 130, 14);
    doc.setFontSize(9);
    doc.text(`Date: ${po.date}`, 130, 20);
    doc.text(`Status: ${po.status.toUpperCase()}`, 130, 26);

    doc.text(`Vendor: ${po.vendor_name}`, 12, 34);
    doc.text(`Zone: ${po.zone}`, 12, 39);
    if (po.delivery_address) doc.text(`Deliver to: ${po.delivery_address}`, 12, 44);

    autoTable(doc, withDefaults({
      startY: 52,
      head: [["#", "Description", "HSN", "Qty", "Unit", "Rate", "Amount"]],
      body: (po.items || []).map((it, i) => [
        i + 1, it.description, it.hsn || "", it.quantity, it.unit || "",
        fmtINR(it.rate), fmtINR(it.amount),
      ]),
      headStyles: { fillColor: [225, 29, 42], textColor: [255,255,255], font: "NotoSans", fontStyle: "bold" },
      columnStyles: { 5: { halign: "right" }, 6: { halign: "right" } },
    }));

    drawTotalsBlock(doc, [
      { label: "Subtotal", value: fmtINR(po.subtotal) },
      { label: `GST (${po.tax_percent}%)`, value: fmtINR(po.tax_amount) },
      { label: "Total", value: fmtINR(po.total) },
    ], { title: "PO totals" });

    finalizePdf(doc, { footer: `Generated by Coke@9 OCT - powered By BBIPL · Payment 30 days from invoice date · ${new Date().toLocaleString("en-IN")}` });
    doc.save(`${po.po_number}.pdf`);
  };

  const filtered = pos.filter(p => {
    if (filters.zone && p.zone !== filters.zone) return false;
    if (filters.vendor && p.vendor_id !== filters.vendor) return false;
    if (filters.status && p.status !== filters.status) return false;
    if (filters.q && !`${p.po_number} ${p.vendor_name}`.toLowerCase().includes(filters.q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid="po-search" placeholder="Search PO # or vendor…" value={filters.q}
            onChange={e => setFilters({ ...filters, q: e.target.value })} className="max-w-xs" />
          <select data-testid="po-zone-filter" value={filters.zone} onChange={e => setFilters({ ...filters, zone: e.target.value })} className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
            <option value="">All zones</option>{zones.map(z => <option key={z.id} value={z.name}>{z.name}</option>)}
          </select>
          <select data-testid="po-vendor-filter" value={filters.vendor} onChange={e => setFilters({ ...filters, vendor: e.target.value })} className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
            <option value="">All vendors</option>{vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select data-testid="po-status-filter" value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
            <option value="">All statuses</option><option value="draft">Draft</option><option value="sent">Sent</option>
            <option value="received">Received</option><option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="flex gap-2">
          <Button data-testid="add-po-btn" onClick={openNew} className="bg-brand-red hover:bg-[#B31721]" disabled={!vendors.length || !zones.length}>
            <Plus className="h-4 w-4 mr-2" /> Create PO
          </Button>
          <Button data-testid="upload-po-btn" onClick={openUpload} variant="outline" disabled={!vendors.length}>
            <Upload className="h-4 w-4 mr-2" /> Upload PO
          </Button>
        </div>
      </div>

      {(!vendors.length || !zones.length) && (
        <Card className="p-4 bg-amber-50 border-amber-200 text-amber-900 text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Add at least one vendor and one zone before creating a PO.
        </Card>
      )}

      <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pos-table">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-3">PO #</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Zone</th>
                <th className="text-left px-4 py-3">Vendor</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Attachment</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-8 text-gray-500">Loading…</td></tr>}
              {!loading && filtered.map(p => (
                <tr key={p.id} className="border-t border-gray-100" data-testid={`po-row-${p.po_number}`}>
                  <td className="px-4 py-3 font-mono text-xs">
                    <div>{p.po_number}</div>
                    <div className="mt-0.5">
                      <Badge className={p.source === "uploaded" ? "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-50 text-[9px] uppercase tracking-widest" : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-50 text-[9px] uppercase tracking-widest"}>
                        {p.source === "uploaded" ? "Uploaded" : "Created"}
                      </Badge>
                    </div>
                  </td>
                  <td className="px-4 py-3">{p.date}</td>
                  <td className="px-4 py-3"><Badge className="bg-red-50 text-brand-red hover:bg-red-50 border border-red-200">{p.zone}</Badge></td>
                  <td className="px-4 py-3">{p.vendor_name}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{formatINR(p.total)}</td>
                  <td className="px-4 py-3">
                    <select data-testid={`po-status-${p.po_number}`} value={p.status}
                      onChange={e => setStatus(p, e.target.value)}
                      className={`text-xs font-semibold rounded-md px-2 py-1 border-0 cursor-pointer ${STATUS_COLORS[p.status] || "bg-gray-100"}`}>
                      <option value="draft">draft</option><option value="sent">sent</option>
                      <option value="received">received</option><option value="cancelled">cancelled</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    {p.attachment_path ? (
                      <Button size="sm" variant="outline" onClick={() => openFile(p.attachment_path)} data-testid={`po-view-attach-${p.po_number}`}>
                        <ExternalLink className="h-3.5 w-3.5 mr-1" /> View
                      </Button>
                    ) : (
                      <label className="cursor-pointer">
                        <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.docx"
                          data-testid={`po-upload-input-${p.po_number}`}
                          onChange={(e) => uploadAttachment(p, e.target.files?.[0])} />
                        <Button size="sm" variant="outline" asChild disabled={uploadingId === p.id}>
                          <span><Paperclip className="h-3.5 w-3.5 mr-1" /> {uploadingId === p.id ? "Uploading…" : "Attach"}</span>
                        </Button>
                      </label>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(p)} className="mr-1" data-testid={`po-pdf-${p.po_number}`}>
                      <Download className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="mr-1" data-testid={`po-edit-${p.po_number}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setConfirmDel(p)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`po-del-${p.po_number}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (<tr><td colSpan={8} className="text-center py-8 text-gray-500">No purchase orders match</td></tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      {form && (
        <Dialog open={open} onOpenChange={(v) => !v && setForm(null)}>
          <DialogContent data-testid="po-dialog" className="max-w-3xl rounded-2xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{form.id ? "Edit purchase order" : "New purchase order"}</DialogTitle></DialogHeader>

            <div className="grid grid-cols-4 gap-3">
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Date</label>
                <Input data-testid="po-date-input" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Zone</label>
                <select data-testid="po-zone-select" value={form.zone} onChange={e => setForm({ ...form, zone: e.target.value })} className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  {zones.map(z => <option key={z.id} value={z.name}>{z.name}</option>)}
                </select></div>
              <div className="col-span-2"><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Vendor</label>
                <select data-testid="po-vendor-select" value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })} className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select></div>
              <div className="col-span-4"><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Delivery address (optional)</label>
                <Input data-testid="po-delivery-input" value={form.delivery_address} onChange={e => setForm({ ...form, delivery_address: e.target.value })} /></div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Line items</div>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="add-po-item-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add row</Button>
              </div>
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="text-left p-2">Description</th>
                      <th className="text-left p-2 w-20">HSN</th>
                      <th className="text-right p-2 w-20">Qty</th>
                      <th className="text-left p-2 w-20">Unit</th>
                      <th className="text-right p-2 w-24">Rate</th>
                      <th className="text-right p-2 w-24">Amount</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => (
                      <tr key={it._uid || `item-${i}`} className="border-t border-gray-100">
                        <td className="p-1"><Input data-testid={`po-item-desc-${i}`} value={it.description} onChange={e => setItem(i, "description", e.target.value)} placeholder="BIB 5L Coca-Cola" /></td>
                        <td className="p-1"><Input data-testid={`po-item-hsn-${i}`} value={it.hsn} onChange={e => setItem(i, "hsn", e.target.value)} /></td>
                        <td className="p-1"><Input data-testid={`po-item-qty-${i}`} inputMode="decimal" value={it.quantity} onChange={e => setItem(i, "quantity", e.target.value.replace(/[^\d.]/g, ""))} className="text-right" /></td>
                        <td className="p-1"><Input data-testid={`po-item-unit-${i}`} value={it.unit} onChange={e => setItem(i, "unit", e.target.value)} /></td>
                        <td className="p-1"><Input data-testid={`po-item-rate-${i}`} inputMode="decimal" value={it.rate} onChange={e => setItem(i, "rate", e.target.value.replace(/[^\d.]/g, ""))} className="text-right" /></td>
                        <td className="p-2 text-right font-mono text-xs">{formatINR((Number(it.quantity) || 0) * (Number(it.rate) || 0))}</td>
                        <td className="p-1 text-right">
                          <button type="button" onClick={() => rmItem(i)} className="text-red-600 hover:text-red-800" data-testid={`po-item-rm-${i}`}><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-4 mt-3 text-sm">
                <div className="text-right space-y-0.5">
                  <div>Subtotal: <span className="font-mono font-semibold">{formatINR(totals.subtotal)}</span></div>
                  <div className="flex items-center gap-1 justify-end">
                    <span>GST</span>
                    <Input data-testid="po-tax-input" inputMode="decimal" value={form.tax_percent}
                      onChange={e => setForm({ ...form, tax_percent: e.target.value.replace(/[^\d.]/g, "") })}
                      className="h-7 w-14 text-right" />
                    <span>%: <span className="font-mono font-semibold">{formatINR(totals.tax)}</span></span>
                  </div>
                  <div className="font-display text-lg font-black text-brand-red">Total: {formatINR(totals.total)}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-2">
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Status</label>
                <select data-testid="po-form-status-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  <option value="draft">draft</option><option value="sent">sent</option><option value="received">received</option><option value="cancelled">cancelled</option>
                </select></div>
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Notes</label>
                <Input data-testid="po-notes-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>

            {/* Attachment upload — parity with Invoice attachment */}
            <div className="mt-2">
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Attachment (PDF/Image)</label>
              <div className="flex items-center gap-2">
                <Input data-testid="po-attachment-input" type="file" accept="application/pdf,image/png,image/jpeg,.pdf,.jpg,.jpeg,.png"
                  disabled={uploadingPoFile}
                  onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    if (file.size > 10 * 1024 * 1024) return toast.error("File too large (max 10 MB)");
                    setUploadingPoFile(true);
                    try {
                      const fd = new FormData(); fd.append("file", file);
                      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setForm(f => ({ ...f, attachment_path: data.path, attachment_name: data.original_name || file.name }));
                      toast.success("Attached");
                    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
                    finally { setUploadingPoFile(false); }
                  }}
                  className="flex-1" />
                {form.attachment_path && (
                  <Button size="sm" variant="outline" onClick={() => openFile(form.attachment_path)} data-testid="po-form-view-attach-btn">
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> View
                  </Button>
                )}
                {form.attachment_path && (
                  <Button size="sm" variant="ghost" onClick={() => setForm(f => ({ ...f, attachment_path: null, attachment_name: null }))}>
                    Remove
                  </Button>
                )}
              </div>
              {form.attachment_name && <div className="text-xs text-gray-500 mt-1" data-testid="po-form-attach-name">{form.attachment_name}</div>}
              <div className="text-[10px] text-gray-400 mt-1">PDF / JPG / PNG · up to 10 MB · same behaviour as Invoice attachment</div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
              <Button data-testid="save-po-btn" onClick={save} disabled={saving} className="bg-brand-red hover:bg-[#B31721]">{form.id ? "Save PO" : "Create PO"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Delete PO?</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700">Delete <b>{confirmDel?.po_number}</b>? Only allowed if no invoices are linked.</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button data-testid="confirm-del-po" onClick={del} className="bg-red-600 hover:bg-red-700 text-white"><Trash2 className="h-4 w-4 mr-2" /> Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload PO Dialog */}
      {uploadPoForm && (
        <Dialog open={!!uploadPoForm} onOpenChange={(v) => !v && setUploadPoForm(null)}>
          <DialogContent data-testid="upload-po-dialog" className="max-w-lg rounded-2xl" aria-describedby="upload-po-desc">
            <DialogHeader><DialogTitle>Upload existing PO</DialogTitle></DialogHeader>
            <div id="upload-po-desc" className="grid grid-cols-2 gap-3">
              <div className="col-span-2 text-xs text-gray-500">Attach a vendor-issued PO document. It counts everywhere a Created PO does — just tagged "Uploaded".</div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Vendor</label>
                <select data-testid="upload-po-vendor" value={uploadPoForm.vendor_id}
                  onChange={e => setUploadPoForm({ ...uploadPoForm, vendor_id: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Zone</label>
                <select data-testid="upload-po-zone" value={uploadPoForm.zone}
                  onChange={e => setUploadPoForm({ ...uploadPoForm, zone: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  {zones.map(z => <option key={z.id} value={z.name}>{z.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Date</label>
                <Input data-testid="upload-po-date" type="date" value={uploadPoForm.date}
                  onChange={e => setUploadPoForm({ ...uploadPoForm, date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Reference / PO number (optional)</label>
                <Input data-testid="upload-po-refnum" placeholder="Vendor's PO #"
                  value={uploadPoForm.po_number}
                  onChange={e => setUploadPoForm({ ...uploadPoForm, po_number: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Total ₹</label>
                <Input data-testid="upload-po-total" inputMode="decimal" value={uploadPoForm.total}
                  onChange={e => setUploadPoForm({ ...uploadPoForm, total: e.target.value.replace(/[^\d.]/g, "") })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">GST % (in total)</label>
                <Input data-testid="upload-po-tax" inputMode="decimal" value={uploadPoForm.tax_percent}
                  onChange={e => setUploadPoForm({ ...uploadPoForm, tax_percent: e.target.value.replace(/[^\d.]/g, "") })} />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Notes (optional)</label>
                <Input data-testid="upload-po-notes" value={uploadPoForm.notes}
                  onChange={e => setUploadPoForm({ ...uploadPoForm, notes: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Attachment (required)</label>
                <div className="flex items-center gap-2">
                  <Input type="file" accept=".pdf,.jpg,.jpeg,.png"
                    data-testid="upload-po-file"
                    disabled={uploadingPoFile}
                    onChange={(e) => pickUploadPoFile(e.target.files?.[0])} />
                  {uploadPoForm.attachment_name && (
                    <Button size="sm" variant="outline" onClick={() => openFile(uploadPoForm.attachment_path)}>
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                  )}
                </div>
                {uploadPoForm.attachment_name && <div className="text-xs text-gray-500 mt-1">{uploadPoForm.attachment_name}</div>}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadPoForm(null)}>Cancel</Button>
              <Button data-testid="save-upload-po-btn" onClick={saveUploadPo}
                disabled={saving || !uploadPoForm.attachment_path}
                className="bg-brand-red hover:bg-[#B31721]">
                <Upload className="h-4 w-4 mr-2" /> Save uploaded PO
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/* ---------- Invoices ---------- */
function InvoicesTab({ invoices, pos, vendors, filter, setFilter, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [uploadInvForm, setUploadInvForm] = useState(null);
  const [uploadingInvFile, setUploadingInvFile] = useState(false);

  const openNew = () => setForm({
    id: null, po_id: pos[0]?.id || "", invoice_number: "",
    date: todayLocalISO(),
    amount: "", tax_amount: "", total: "", payment_status: "unpaid",
    paid_amount: "0", paid_at: "", notes: "", attachment_path: null, attachment_name: null,
  });
  const openUpload = () => setUploadInvForm({
    po_id: "",   // optional
    vendor_id: vendors[0]?.id || "",
    zone: "",
    invoice_number: "",
    date: todayLocalISO(),
    total: "", tax_amount: "",
    payment_status: "unpaid",
    notes: "",
    attachment_path: null, attachment_name: null,
  });
  useEffect(() => { if (form) setOpen(true); else setOpen(false); }, [form]);

  const pickUploadInvFile = async (file) => {
    if (!file) return;
    setUploadingInvFile(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload-doc", fd);
      setUploadInvForm(f => ({ ...f, attachment_path: data.path, attachment_name: data.original_name }));
      toast.success("File attached");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploadingInvFile(false); }
  };
  const saveUploadInv = async () => {
    const f = uploadInvForm;
    if (!f.invoice_number.trim()) return toast.error("Invoice number required");
    if (!f.attachment_path) return toast.error("Attach the invoice document");
    if (!(Number(f.total) > 0)) return toast.error("Total must be > 0");
    if (!f.po_id && !f.vendor_id) return toast.error("Pick a PO or a Vendor");
    setSaving(true);
    try {
      await api.post("/invoices/upload", {
        po_id: f.po_id || null, vendor_id: f.vendor_id || null, zone: f.zone,
        invoice_number: f.invoice_number.trim(), date: f.date,
        total: Number(f.total), tax_amount: Number(f.tax_amount) || 0,
        payment_status: f.payment_status, notes: f.notes,
        attachment_path: f.attachment_path, attachment_name: f.attachment_name,
      });
      toast.success("Invoice uploaded"); setUploadInvForm(null); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const openEdit = (inv) => setForm({
    ...inv, amount: String(inv.amount), tax_amount: String(inv.tax_amount || 0),
    total: String(inv.total), paid_amount: String(inv.paid_amount || 0),
    paid_at: inv.paid_at || "",
  });

  const save = async () => {
    if (!form.po_id) return toast.error("PO required");
    if (!form.invoice_number.trim()) return toast.error("Invoice number required");
    setSaving(true);
    try {
      const total = Number(form.total) || (Number(form.amount) + Number(form.tax_amount || 0));
      const payload = {
        po_id: form.po_id, invoice_number: form.invoice_number.trim(),
        date: form.date, amount: Number(form.amount) || 0,
        tax_amount: Number(form.tax_amount) || 0, total,
        payment_status: form.payment_status,
        paid_amount: Number(form.paid_amount) || 0,
        paid_at: form.paid_at || null,
        notes: form.notes,
        attachment_path: form.attachment_path, attachment_name: form.attachment_name,
      };
      if (form.id) { await api.patch(`/invoices/${form.id}`, payload); toast.success("Invoice updated"); }
      else { await api.post("/invoices", payload); toast.success("Invoice recorded"); }
      setForm(null); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!confirmDel) return;
    try { await api.delete(`/invoices/${confirmDel.id}`); toast.success("Deleted"); setConfirmDel(null); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const uploadInline = async (file) => {
    if (!file || !form) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload-doc", fd);
      setForm(f => ({ ...f, attachment_path: data.path, attachment_name: data.original_name }));
      toast.success("Attached");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const openFile = (path) => {
    const token = localStorage.getItem("bb_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`, "_blank");
  };

  const filtered = invoices.filter(i => {
    if (filter.vendor && i.vendor_id !== filter.vendor) return false;
    if (filter.payment_status && i.payment_status !== filter.payment_status) return false;
    if (filter.q && !`${i.invoice_number} ${i.po_number} ${i.vendor_name}`.toLowerCase().includes(filter.q.toLowerCase())) return false;
    return true;
  });

  const selectedPo = form && pos.find(p => p.id === form.po_id);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid="inv-search" placeholder="Search invoice / PO / vendor…"
            value={filter.q} onChange={e => setFilter({ ...filter, q: e.target.value })} className="max-w-xs" />
          <select data-testid="inv-vendor-filter" value={filter.vendor} onChange={e => setFilter({ ...filter, vendor: e.target.value })} className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
            <option value="">All vendors</option>{vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select data-testid="inv-payment-filter" value={filter.payment_status} onChange={e => setFilter({ ...filter, payment_status: e.target.value })} className="h-10 rounded-md border border-gray-200 px-3 text-sm bg-white">
            <option value="">All payments</option><option value="unpaid">Unpaid</option><option value="partial">Partial</option><option value="paid">Paid</option>
          </select>
        </div>
        <div className="flex gap-2">
          <Button data-testid="add-invoice-btn" onClick={openNew} disabled={!pos.length} className="bg-brand-red hover:bg-[#B31721]">
            <Plus className="h-4 w-4 mr-2" /> Create Invoice
          </Button>
          <Button data-testid="upload-invoice-btn" onClick={openUpload} variant="outline" disabled={!vendors.length && !pos.length}>
            <Upload className="h-4 w-4 mr-2" /> Upload Invoice
          </Button>
        </div>
      </div>

      {!pos.length && (
        <Card className="p-4 bg-amber-50 border-amber-200 text-amber-900 text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Create a purchase order first to record invoices against it.
        </Card>
      )}

      <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-3">Invoice #</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">PO #</th>
                <th className="text-left px-4 py-3">Vendor</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Match PO</th>
                <th className="text-left px-4 py-3">Payment</th>
                <th className="text-left px-4 py-3">File</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center py-8 text-gray-500">Loading…</td></tr>}
              {!loading && filtered.map(inv => (
                <tr key={inv.id} className="border-t border-gray-100" data-testid={`inv-row-${inv.invoice_number}`}>
                  <td className="px-4 py-3 font-mono text-xs">
                    <div>{inv.invoice_number}</div>
                    <div className="mt-0.5">
                      <Badge className={inv.source === "uploaded" ? "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-50 text-[9px] uppercase tracking-widest" : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-50 text-[9px] uppercase tracking-widest"}>
                        {inv.source === "uploaded" ? "Uploaded" : "Created"}
                      </Badge>
                    </div>
                  </td>
                  <td className="px-4 py-3">{inv.date}</td>
                  <td className="px-4 py-3 font-mono text-xs">{inv.po_number}</td>
                  <td className="px-4 py-3">{inv.vendor_name}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{formatINR(inv.total)}</td>
                  <td className="px-4 py-3">
                    {inv.amount_matches_po
                      ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">✓ Match</Badge>
                      : <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`inv-mismatch-${inv.invoice_number}`}>≠ Mismatch</Badge>}
                  </td>
                  <td className="px-4 py-3"><Badge className={`${STATUS_COLORS[inv.payment_status] || "bg-gray-100"} hover:${STATUS_COLORS[inv.payment_status] || "bg-gray-100"}`}>{inv.payment_status}</Badge></td>
                  <td className="px-4 py-3">
                    {inv.attachment_path
                      ? <Button size="sm" variant="outline" onClick={() => openFile(inv.attachment_path)}><ExternalLink className="h-3.5 w-3.5 mr-1" /> View</Button>
                      : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openEdit(inv)} className="mr-1" data-testid={`inv-edit-${inv.invoice_number}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => setConfirmDel(inv)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`inv-del-${inv.invoice_number}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (<tr><td colSpan={9} className="text-center py-8 text-gray-500">No invoices match</td></tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      {form && (
        <Dialog open={open} onOpenChange={(v) => !v && setForm(null)}>
          <DialogContent data-testid="invoice-dialog" className="max-w-xl rounded-2xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{form.id ? "Edit invoice" : "Record invoice"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Purchase order</label>
                <select data-testid="inv-po-select" value={form.po_id} onChange={e => setForm({ ...form, po_id: e.target.value })}
                  disabled={!!form.id}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  {pos.map(p => <option key={p.id} value={p.id}>{p.po_number} · {p.vendor_name} · {formatINR(p.total)}</option>)}
                </select>
                {selectedPo && <div className="text-xs text-gray-500 mt-1">PO total: <span className="font-mono">{formatINR(selectedPo.total)}</span></div>}
              </div>
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Invoice number</label>
                <Input data-testid="inv-num-input" value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} placeholder="INV-2026-001" /></div>
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Date</label>
                <Input data-testid="inv-date-input" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Amount (pre-tax)</label>
                <Input data-testid="inv-amount-input" inputMode="decimal" value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value.replace(/[^\d.]/g, "") })} /></div>
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Tax amount</label>
                <Input data-testid="inv-tax-input" inputMode="decimal" value={form.tax_amount}
                  onChange={e => setForm({ ...form, tax_amount: e.target.value.replace(/[^\d.]/g, "") })} /></div>
              <div className="col-span-2"><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Total (auto if empty)</label>
                <Input data-testid="inv-total-input" inputMode="decimal" value={form.total}
                  onChange={e => setForm({ ...form, total: e.target.value.replace(/[^\d.]/g, "") })} placeholder={`${Number(form.amount||0) + Number(form.tax_amount||0)}`} /></div>
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Payment status</label>
                <select data-testid="inv-payment-select" value={form.payment_status} onChange={e => setForm({ ...form, payment_status: e.target.value })} className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  <option value="unpaid">unpaid</option><option value="partial">partial</option><option value="paid">paid</option>
                </select></div>
              <div><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Paid amount</label>
                <Input data-testid="inv-paid-amount-input" inputMode="decimal" value={form.paid_amount}
                  onChange={e => setForm({ ...form, paid_amount: e.target.value.replace(/[^\d.]/g, "") })} /></div>
              <div className="col-span-2"><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Attachment (PDF/image)</label>
                <div className="flex items-center gap-2">
                  <Input data-testid="inv-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.docx"
                    onChange={e => uploadInline(e.target.files?.[0])} className="flex-1" />
                  {form.attachment_path && <Button size="sm" variant="outline" onClick={() => openFile(form.attachment_path)}><ExternalLink className="h-3.5 w-3.5 mr-1" /> View</Button>}
                </div>
                {form.attachment_name && <div className="text-xs text-gray-500 mt-1">{form.attachment_name}</div>}
              </div>
              <div className="col-span-2"><label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Notes</label>
                <Input data-testid="inv-notes-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
              <Button data-testid="save-invoice-btn" onClick={save} disabled={saving} className="bg-brand-red hover:bg-[#B31721]">{form.id ? "Save" : "Record"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Delete invoice?</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700">Delete invoice <b>{confirmDel?.invoice_number}</b>?</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button data-testid="confirm-del-invoice" onClick={del} className="bg-red-600 hover:bg-red-700 text-white"><Trash2 className="h-4 w-4 mr-2" /> Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Invoice Dialog */}
      {uploadInvForm && (
        <Dialog open={!!uploadInvForm} onOpenChange={(v) => !v && setUploadInvForm(null)}>
          <DialogContent data-testid="upload-invoice-dialog" className="max-w-lg rounded-2xl" aria-describedby="upload-inv-desc">
            <DialogHeader><DialogTitle>Upload existing Invoice</DialogTitle></DialogHeader>
            <div id="upload-inv-desc" className="grid grid-cols-2 gap-3">
              <div className="col-span-2 text-xs text-gray-500">Attach a vendor-issued invoice document. Counts everywhere a Created invoice does — just tagged "Uploaded".</div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Link to existing PO (optional)</label>
                <select data-testid="upload-inv-po" value={uploadInvForm.po_id}
                  onChange={e => setUploadInvForm({ ...uploadInvForm, po_id: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white">
                  <option value="">— none — (standalone)</option>
                  {pos.map(p => <option key={p.id} value={p.id}>{p.po_number} · {p.vendor_name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Vendor {uploadInvForm.po_id ? "(from PO)" : "(required)"}</label>
                <select data-testid="upload-inv-vendor" value={uploadInvForm.vendor_id}
                  onChange={e => setUploadInvForm({ ...uploadInvForm, vendor_id: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 px-3 text-sm bg-white"
                  disabled={!!uploadInvForm.po_id}>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Invoice number</label>
                <Input data-testid="upload-inv-refnum" value={uploadInvForm.invoice_number}
                  onChange={e => setUploadInvForm({ ...uploadInvForm, invoice_number: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Date</label>
                <Input data-testid="upload-inv-date" type="date" value={uploadInvForm.date}
                  onChange={e => setUploadInvForm({ ...uploadInvForm, date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Total ₹</label>
                <Input data-testid="upload-inv-total" inputMode="decimal" value={uploadInvForm.total}
                  onChange={e => setUploadInvForm({ ...uploadInvForm, total: e.target.value.replace(/[^\d.]/g, "") })} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">GST amount (₹)</label>
                <Input data-testid="upload-inv-tax" inputMode="decimal" value={uploadInvForm.tax_amount}
                  onChange={e => setUploadInvForm({ ...uploadInvForm, tax_amount: e.target.value.replace(/[^\d.]/g, "") })} />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Notes</label>
                <Input data-testid="upload-inv-notes" value={uploadInvForm.notes}
                  onChange={e => setUploadInvForm({ ...uploadInvForm, notes: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Attachment (required)</label>
                <div className="flex items-center gap-2">
                  <Input type="file" accept=".pdf,.jpg,.jpeg,.png"
                    data-testid="upload-inv-file"
                    disabled={uploadingInvFile}
                    onChange={(e) => pickUploadInvFile(e.target.files?.[0])} />
                  {uploadInvForm.attachment_name && (
                    <Button size="sm" variant="outline" onClick={() => openFile(uploadInvForm.attachment_path)}>
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                  )}
                </div>
                {uploadInvForm.attachment_name && <div className="text-xs text-gray-500 mt-1">{uploadInvForm.attachment_name}</div>}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadInvForm(null)}>Cancel</Button>
              <Button data-testid="save-upload-invoice-btn" onClick={saveUploadInv}
                disabled={saving || !uploadInvForm.attachment_path}
                className="bg-brand-red hover:bg-[#B31721]">
                <Upload className="h-4 w-4 mr-2" /> Save uploaded invoice
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
